import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';
import { PawPrint, Weight, AlertTriangle, Baby, Package, Calendar, TrendingUp, Activity, Shield } from 'lucide-react';
import api from '../utils/api';
import { formatDateBR } from '../utils/formatDate';
import { formatGd } from '../utils/gmd';

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [modalData, setModalData] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);
  const [modalEmptyMsg, setModalEmptyMsg] = useState('Nenhum registro encontrado.');
  const navigate = useNavigate();

  useEffect(() => { api.get('/dashboard').then(r => setData(r.data)).finally(() => setLoading(false)); }, []);

  if (loading) return <div style={{padding:40,textAlign:'center',color:'var(--text-secondary)'}}>Carregando dashboard...</div>;

  const handleCardClick = async (tipo) => {
    switch (tipo) {
      case 'rebanho':
        setModalLoading(true); setModal('Resumo do Rebanho na Cabaña');
        try { const r = await api.get('/dashboard/detalhe/rebanho'); setModalData(r.data); } catch {}
        setModalLoading(false); break;
      case 'gmd': navigate('/relatorios/gmd'); break;
      case 'mortalidade':
        setModalLoading(true); setModal('Mortalidade (óbitos registrados)');
        setModalEmptyMsg('Nenhum óbito registrado.');
        try { const r = await api.get('/dashboard/detalhe/mortalidade'); setModalData(r.data); } catch {}
        setModalLoading(false); break;
      case 'obito_precoce':
        setModalLoading(true); setModal('Óbito Precoce (nascidos na propriedade, óbito < 30 dias)');
        setModalEmptyMsg('Nenhum óbito precoce registrado.');
        try { const r = await api.get('/dashboard/detalhe/obito-precoce'); setModalData(r.data); } catch {}
        setModalLoading(false); break;
      case 'alertas': navigate('/descartes'); break;
      case 'cordeiros':
        setModalLoading(true); setModal('Cordeiros');
        try { const r = await api.get('/dashboard/detalhe/cordeiros'); setModalData(r.data); } catch {}
        setModalLoading(false); break;
      case 'estoque_critico':
        setModalLoading(true); setModal('Estoque Crítico');
        try { const r = await api.get('/dashboard/detalhe/estoque-critico'); setModalData(r.data); } catch {}
        setModalLoading(false); break;
      case 'tratamento':
        setModalLoading(true); setModal('Animais em Tratamento / Observação');
        try { const r = await api.get('/dashboard/detalhe/em-tratamento'); setModalData(r.data); } catch {}
        setModalLoading(false); break;
      default: break;
    }
  };

  const fecharModal = () => { setModal(null); setModalData(null); };

  const totalAlertas = (data?.alertas_total || 0) + (data?.desmame_alertas?.length || 0) + (data?.alertas_famacha?.length || 0) + (data?.carencias_ativas?.length || 0) + (data?.transicao_amamentacao?.length || 0);

  const cards = [
    { label:'Na Cabaña', value: data?.rebanho?.total || 0, sub: `${data?.rebanho?.machos||0} machos · ${data?.rebanho?.femeas||0} fêmeas`, icon: PawPrint, color:'#2D6A4F', bg:'#DCFCE7', tipo: 'rebanho' },
    { label:'GMD Médio', value: data?.gmd_medio ? formatGd(data.gmd_medio) : '—', sub: 'ganho médio diário', icon: TrendingUp, color:'#2563EB', bg:'#DBEAFE', tipo: 'gmd' },
    { label:'Mortalidade', value: `${data?.mortalidade?.percentual || 0}%`, sub: `${data?.mortalidade?.obitos||0} óbitos · base ${data?.mortalidade?.base||0} (todo o rebanho)`, icon: Activity, color:'#DC2626', bg:'#FEE2E2', tipo: 'mortalidade' },
    { label:'Óbito Precoce', value: `${data?.obito_precoce?.percentual || 0}%`, sub: `${data?.obito_precoce?.obitos||0} óbitos <30d · base ${data?.obito_precoce?.base||0} (nascidos)`, icon: Activity, color:'#9333EA', bg:'#F3E8FF', tipo: 'obito_precoce' },
    { label:'Alertas', value: totalAlertas, sub: 'famacha · descarte · desmame · carência', icon: AlertTriangle, color:'#D97706', bg:'#FEF3C7', tipo: 'alertas' },
    { label:'Cordeiros', value: data?.rebanho?.cordeiros || 0, sub: `${data?.rebanho?.cordeiros_machos||0} machos · ${data?.rebanho?.cordeiros_femeas||0} fêmeas`, icon: Baby, color:'#7C3AED', bg:'#EDE9FE', tipo: 'cordeiros' },
    { label:'Estoque Crítico', value: data?.estoque_critico?.length || 0, sub: 'produtos abaixo do mínimo', icon: Package, color:'#EA580C', bg:'#FFEDD5', tipo: 'estoque_critico' },
    { label:'Em Tratamento', value: data?.em_tratamento || 0, sub: 'carência sanitária ativa', icon: Shield, color:'#0891B2', bg:'#CFFAFE', tipo: 'tratamento' },
  ];

  return (
    <div>
      <div className="page-header">
        <h1>Dashboard</h1>
        <p>Visão geral do rebanho — {new Date().toLocaleDateString('pt-BR', {weekday:'long', day:'numeric', month:'long', year:'numeric'})}</p>
      </div>

      <div className="stats-grid">
        {cards.map(c => (
          <div key={c.label} className="stat-card" style={{borderLeft:`4px solid ${c.color}`, cursor:'pointer'}} onClick={() => handleCardClick(c.tipo)}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start'}}>
              <div>
                <div className="stat-label">{c.label}</div>
                <div className="stat-value" style={{color:c.color}}>{c.value}</div>
                <div className="stat-sub">{c.sub}</div>
              </div>
              <div style={{padding:'10px',borderRadius:'10px',background:c.bg}}>
                <c.icon size={20} color={c.color} />
              </div>
            </div>
          </div>
        ))}
      </div>

      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'20px',marginBottom:'20px'}}>
        <div className="card">
          <h3 style={{marginBottom:'16px',display:'flex',alignItems:'center',gap:'8px',fontSize:'15px',fontWeight:'600'}}>
            <Calendar size={16} color="var(--color-primary)" /> Próximos 7 dias
          </h3>
          {(data?.agenda_pendente?.length || data?.intervencoes_pendentes?.length) ? (
            <div style={{display:'flex',flexDirection:'column',gap:'8px'}}>
              {data.agenda_pendente?.map(ev => (
                <div key={`ag-${ev.id}`} style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'10px',background:'var(--bg-main)',borderRadius:'8px'}}>
                  <div>
                    <div style={{fontSize:'14px',fontWeight:'500'}}>{ev.titulo}</div>
                    <div style={{fontSize:'12px',color:'var(--text-secondary)'}}>{new Date(ev.data_inicio).toLocaleString('pt-BR')}</div>
                  </div>
                  <span className={`badge badge-${ev.tipo==='vacinacao'?'blue':ev.tipo==='parto_previsto'?'orange':'green'}`}>{ev.tipo.replace(/_/g,' ')}</span>
                </div>
              ))}
              {data.intervencoes_pendentes?.map(iv => (
                <div key={`iv-${iv.id}`} style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'10px',background:'#FFF7ED',borderRadius:'8px',borderLeft:'3px solid #EA580C'}}>
                  <div>
                    <div style={{fontSize:'14px',fontWeight:'500'}}>{iv.brinco}{iv.animal_nome ? ` — ${iv.animal_nome}` : ''}</div>
                    <div style={{fontSize:'12px',color:'var(--text-secondary)'}}>
                      {formatDateBR(iv.data)}
                      {iv.produto_nome ? ` · ${iv.produto_nome}` : ''}
                    </div>
                  </div>
                  <span className="badge badge-orange">{iv.tipo}</span>
                </div>
              ))}
            </div>
          ) : <p style={{color:'var(--text-secondary)',fontSize:'14px'}}>Nenhum evento nos próximos 7 dias</p>}
        </div>

        <div className="card">
          <h3 style={{marginBottom:'16px',display:'flex',alignItems:'center',gap:'8px',fontSize:'15px',fontWeight:'600'}}>
            <Baby size={16} color="var(--color-primary)" /> Partos Previstos (20 dias)
          </h3>
          {data?.partos_previstos?.length ? (
            <table>
              <thead><tr><th>Matriz</th><th>Previsão</th></tr></thead>
              <tbody>
                {data.partos_previstos.map((p,i) => (
                  <tr key={i}>
                    <td><strong>{p.brinco}</strong> {p.mae && `— ${p.mae}`}</td>
                    <td>{formatDateBR(p.parto_previsto)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : <p style={{color:'var(--text-secondary)',fontSize:'14px'}}>Nenhum parto previsto</p>}
        </div>
      </div>

      <div className="card">
        <h3 style={{marginBottom:'16px',fontSize:'15px',fontWeight:'600',display:'flex',alignItems:'center',gap:'8px'}}>
          <AlertTriangle size={16} color="#D97706" /> Alertas ({totalAlertas})
        </h3>
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(240px,1fr))',gap:'20px'}}>

          <div>
            <div style={{fontWeight:600,fontSize:12,color:'#0369A1',marginBottom:8,textTransform:'uppercase',letterSpacing:0.5}}>🍼 Iniciar Amamentação Controlada</div>
            {data?.transicao_amamentacao?.length ? (
              <div style={{display:'flex',flexDirection:'column',gap:'6px'}}>
                {data.transicao_amamentacao.map((a,i) => (
                  <div key={i} style={{fontSize:'13px'}}>
                    <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                      <div><strong>{a.brinco}</strong>{a.nome && ` — ${a.nome}`}</div>
                      <span className="badge badge-blue">{a.idade_dias}d</span>
                    </div>
                    <div style={{color:'var(--text-secondary)',fontSize:'11px'}}>Cordeiro com 30 dias: iniciar Amamentação Controlada. Encerra-se o período de Amamentação Livre.</div>
                  </div>
                ))}
              </div>
            ) : <p style={{color:'var(--text-secondary)',fontSize:'13px'}}>Sem alertas</p>}
          </div>

          <div>
            <div style={{fontWeight:600,fontSize:12,color:'#D97706',marginBottom:8,textTransform:'uppercase',letterSpacing:0.5}}>⏰ Desmame Pendente</div>
            {data?.desmame_alertas?.length ? (
              <div style={{display:'flex',flexDirection:'column',gap:'6px'}}>
                {data.desmame_alertas.map((a,i) => (
                  <div key={i} style={{display:'flex',justifyContent:'space-between',alignItems:'center',fontSize:'13px'}}>
                    <div><strong>{a.brinco}</strong>{a.nome && ` — ${a.nome}`}</div>
                    <span className={`badge ${a.idade_dias>=70?'badge-red':'badge-yellow'}`}>{a.idade_dias}d</span>
                  </div>
                ))}
              </div>
            ) : <p style={{color:'var(--text-secondary)',fontSize:'13px'}}>Sem alertas</p>}
          </div>

          <div>
            <div style={{fontWeight:600,fontSize:12,color:'#0891B2',marginBottom:8,textTransform:'uppercase',letterSpacing:0.5}}>🛡️ Carências Ativas</div>
            {data?.carencias_ativas?.length ? (
              <div style={{display:'flex',flexDirection:'column',gap:'6px'}}>
                {data.carencias_ativas.map((c,i) => (
                  <div key={i} style={{fontSize:'13px'}}>
                    <div><strong>{c.brinco}</strong> — {c.produto}</div>
                    <div style={{color:'var(--text-secondary)',fontSize:'12px'}}>Até: {formatDateBR(c.data_carencia_fim)}</div>
                  </div>
                ))}
              </div>
            ) : <p style={{color:'var(--text-secondary)',fontSize:'13px'}}>Sem carências</p>}
          </div>

          <div>
            <div style={{fontWeight:600,fontSize:12,color:'#991B1B',marginBottom:8,textTransform:'uppercase',letterSpacing:0.5,display:'flex',alignItems:'center',gap:5}}>
              <span style={{width:10,height:10,borderRadius:'50%',background:'#F5A0A0',display:'inline-block'}} /> Famacha ≥ 3
            </div>
            {data?.alertas_famacha?.length ? (
              <div style={{display:'flex',flexDirection:'column',gap:'6px'}}>
                {data.alertas_famacha.map((a,i) => {
                  const cor = a.escore===3?'#92400E':a.escore===4?'#7C2D12':'#991B1B';
                  const bg  = a.escore===3?'#FFF1F1':a.escore===4?'#FFF8F5':'#FEE2E2';
                  const label = a.escore===3?'Atenção':a.escore===4?'Trat. recomendado':'Trat. urgente';
                  return (
                    <div key={i} style={{display:'flex',justifyContent:'space-between',alignItems:'center',fontSize:'13px'}}>
                      <div><strong>{a.brinco}</strong>{a.nome && ` — ${a.nome}`}</div>
                      <span style={{padding:'2px 7px',borderRadius:'10px',fontSize:'11px',fontWeight:'700',color:cor,background:bg,border:`1px solid ${cor}40`,whiteSpace:'nowrap'}}>{a.escore} · {label}</span>
                    </div>
                  );
                })}
              </div>
            ) : <p style={{color:'var(--text-secondary)',fontSize:'13px'}}>Sem alertas</p>}
          </div>

          <div>
            <div style={{fontWeight:600,fontSize:12,color:'#DC2626',marginBottom:8,textTransform:'uppercase',letterSpacing:0.5}}>⚠️ Alertas de Descarte</div>
            {data?.descarte_lista?.length ? (
              <div style={{display:'flex',flexDirection:'column',gap:'6px'}}>
                {data.descarte_lista.map((d,i) => (
                  <div key={i} style={{display:'flex',justifyContent:'space-between',alignItems:'center',fontSize:'13px'}}>
                    <div><strong>{d.brinco}</strong>{d.animal_nome && ` — ${d.animal_nome}`}</div>
                    <span className={`badge ${d.classificacao==='candidato'?'badge-red':'badge-yellow'}`}>
                      {d.classificacao==='candidato'?'Descarte':'Atenção'}
                    </span>
                  </div>
                ))}
              </div>
            ) : <p style={{color:'var(--text-secondary)',fontSize:'13px'}}>Nenhum alerta</p>}
          </div>

        </div>
      </div>

      {/* Modal de detalhes */}
      {modal && (
        <div className="modal-overlay" onClick={fecharModal}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{maxWidth:'650px',maxHeight:'80vh',overflow:'auto'}}>
            <div className="modal-header"><h2>{modal}</h2><button className="btn btn-secondary btn-sm" onClick={fecharModal}>✕</button></div>
            <div className="modal-body">
              {modalLoading ? <div style={{textAlign:'center',padding:30,color:'var(--text-secondary)'}}>Carregando...</div>
              : !modalData ? <p>Nenhum dado encontrado.</p>
              : modalData.categorias ? (
                <div>
                  <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit, minmax(120px, 1fr))',gap:10,marginBottom:16}}>
                    {Object.entries(modalData.categorias).map(([cat, qtd]) => (
                      <div key={cat} className="card" style={{padding:12,textAlign:'center'}}>
                        <div style={{fontSize:22,fontWeight:'bold',color:'var(--color-primary)'}}>{qtd}</div>
                        <div style={{fontSize:12,color:'var(--text-secondary)',textTransform:'capitalize'}}>{cat}</div>
                      </div>
                    ))}
                  </div>
                  <div style={{textAlign:'center',fontSize:14,color:'var(--text-secondary)'}}>Total: <strong>{modalData.total}</strong> animais na cabaña</div>
                </div>
              ) : Array.isArray(modalData) ? (
                modalData.length === 0 ? (
                  <p style={{textAlign:'center',padding:24,color:'var(--text-secondary)'}}>{modalEmptyMsg}</p>
                ) : (
                <table style={{width:'100%',fontSize:13}}>
                  <thead><tr style={{borderBottom:'2px solid var(--border-color)'}}><th style={{padding:8,textAlign:'left'}}>Brinco</th><th style={{padding:8,textAlign:'left'}}>Nome</th><th style={{padding:8,textAlign:'left'}}>Raça</th><th style={{padding:8,textAlign:'left'}}>Categoria</th></tr></thead>
                  <tbody>
                    {modalData.map((a, i) => (
                      <tr key={i} style={{borderBottom:'1px solid var(--border-color)'}}>
                        <td style={{padding:8}}><strong>{a.brinco||'-'}</strong></td>
                        <td style={{padding:8}}>{a.nome||'-'}</td>
                        <td style={{padding:8}}>{a.raca_nome||'-'}</td>
                        <td style={{padding:8,textTransform:'capitalize'}}>{a.categoria||a.status_amamentacao||'-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                )
              ) : <pre style={{fontSize:12}}>{JSON.stringify(modalData, null, 2)}</pre>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
